import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";
import clsx from "clsx";

import { isRecent } from "../../../libs/toilet";
import { isArrayEmpty } from "../../../libs/common";

import {
  FloorToiletInfo,
  ToiletCompartmentInfo,
  ToiletInfo,
  getToiletMonitoringInfo,
} from "../../../services/iotServices";

import { useUserContext } from "../../../contexts/userContext";

import useToTop from "../../../hooks/useToTop";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";
import useSimplePopup from "../../../hooks/useSimplePopup";

import StickyHeader from "../../../components/Layout/StickyHeader";
import CommonButton from "../../../components/Button";
import HorizontalRule from "../../../components/Rule/Horizontal";
import ReportEmptyToilet from "../../../components/Popup/ReportEmptyToilet";
import IotWorkingFloor from "../../../components/Service/Iot/WorkingFloorBadge";

import { NoDataIcon } from "../../Center";

type Props = {};

export const compartmentIsOpen = (compartment: ToiletCompartmentInfo) => {
  return !(
    compartment?.tag?.[0]?.value === 0 || //상태가 닫힘일때
    (compartment?.tag?.[0]?.value === null && //상태값이 없는데 출입횟수가 있을때
      compartment?.tag?.[0]?.count > 0) ||
    (compartment?.tag?.[0]?.value === null && //상태값이 없는데 최근 출입시간이 있을때
      !isRecent(compartment?.tag?.[0]?.updatedAt))
  );
};

export const isCleaning = (toilet: ToiletInfo | undefined) => {
  return toilet?.clean[0]?.value === 2 && isRecent(toilet?.clean[0]?.updatedAt)
    ? true
    : false;
};

export default function ToiletEmpty(props: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [toiletList, setToiletList] = useState<FloorToiletInfo[]>([]);
  const [user] = useUserContext();
  const [isLoading, setIsLoading] = useState(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [currentFloor, setCurrentFloor] = useState<FloorToiletInfo>();
  const scrollDivRef = React.useRef<HTMLDivElement>(null);
  const toTopComponent = useToTop({
    scrollDivRef,
    wrapperClassName: "bottom-8",
  });
  const [popup, setPopup] = useState<"reportEmptyToilet" | "">(
    location?.state?.popup
  );
  const [simplePopupComponent, { open }] = useSimplePopup({});

  const loadMonitoringToiletData = async () => {
    try {
      setIsLoading(true);
      const res = await getToiletMonitoringInfo({
        buildingId: user.buildingId,
      });
      if (res.status === HttpStatusCode.Ok) {
        await setToiletList(
          res.data[0]?.floors?.map((floors) => ({
            ...floors,
            toilets: floors.toilets.filter(
              (toilet) =>
                toilet.gender === user.gender &&
                toilet.compartments &&
                toilet.compartments.length > 0
            ),
          }))
        );
        await setCurrentFloor(
          res.data[0].floors?.find(
            (toilet) => toilet.buildingFloorId === user.buildingFloorId
          )
        );
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("건물 화장실 정보를 불러오는데 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user.isLoading === false && user.buildingId) {
      loadMonitoringToiletData();
    }
  }, [user.isLoading, user.buildingId]);

  const timeInterval = 30;

  useEffect(() => {
    const interval = setInterval(() => {
      if (user.isLoading === false && user.buildingId) {
        loadMonitoringToiletData();
      }
    }, timeInterval * 1000);

    return () => clearInterval(interval);
  }, [user.isLoading, user.buildingId]);

  return (
    <div className="px-5 flex flex-col gap-5" ref={scrollDivRef}>
      {loadingComponent}
      <StickyHeader
        title="화장실 빈칸 어디?"
        onBackClick={() => navigate("/view")}
      />
      <div className="flex flex-col gap-5">
        <div className="flex justify-between items-center">
          <div className="text-lg">근무 층 {currentFloor?.floorName}</div>
          <CommonButton
            variant="secondary"
            className="px-4 py-[11px] h-8 flex justify-center items-center text-sm font-medium leading-[17px] rounded-[3px]"
            onClick={() => setPopup("reportEmptyToilet")}
          >
            불편신고
          </CommonButton>
        </div>
        {!isArrayEmpty(
          currentFloor?.toilets?.filter(
            (toilet) =>
              toilet.gender === user.gender &&
              toilet.compartments &&
              !isArrayEmpty(toilet.compartments)
          )
        ) && (
          <div className="flex flex-col gap-5">
            {currentFloor?.toilets
              .filter(
                (toilet) =>
                  toilet.gender === user.gender &&
                  toilet.compartments &&
                  toilet.compartments.length > 0
              )
              .map((space) => (
                <React.Fragment key={space.spaceToiletId}>
                  <div className="flex gap-1 items-center">
                    <ToiletEmptyLocationIcon /> {space.toiletName}
                  </div>
                  {isCleaning(space) ? (
                    <div className="flex flex-col gap-1 items-center py-5 text-center text-sm">
                      <LargeCleaningIcon />
                      청소중입니다. <br />
                      다른화장실을 이용하세요
                    </div>
                  ) : (
                    <div className="grid grid-cols-5 gap-y-5">
                      {space?.compartments?.map((compartment) => {
                        return (
                          <React.Fragment key={compartment.toiletCompartmentId}>
                            {compartmentIsOpen(compartment) ? (
                              <ToiletEmptyDoorOpenIcon />
                            ) : (
                              <ToiletEmptyDoorCloseIcon />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </React.Fragment>
              ))}
          </div>
        )}
      </div>
      <div>
        <HorizontalRule margin="0" />
        <div className="pt-[30px] flex flex-col justify-end">
          <div className="flex justify-between flex-wrap">
            <div className="text-lg font-medium">층 별 화장실 혼잡도</div>
            <div className="flex gap-3 items-center text-sm text-brand-neutral-80">
              <div className="flex items-center gap-1">
                <SmallCleaningIcon />
                청소중
              </div>
              <div className="flex items-center gap-1">
                <SmallSmileIcon />
                여유
              </div>
              <div className="flex items-center gap-1">
                <SmallAngryIcon />
                혼잡
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-brand-talk-search">
        <HorizontalRule className="bg-black border-[2px]" margin="0" />
        {toiletList?.filter((floor) => floor.toilets.length > 0)?.length &&
        toiletList?.filter((floor) => floor.toilets.length > 0)?.length > 0 ? (
          toiletList
            ?.filter((floor) => floor.toilets.length > 0)
            .map((floor, index, list) => {
              return (
                <React.Fragment key={floor.buildingFloorId}>
                  <div className="flex items-center h-full">
                    <div
                      className="min-w-[81px] min-h-[60px] h-full flex justify-center items-center text-lg font-semibold flex-col"
                      style={{ height: "100%" }}
                    >
                      {floor.floorName}
                      <IotWorkingFloor
                        buildingFloorId={floor.buildingFloorId}
                        userBuildingFloorId={user.buildingFloorId}
                      />
                    </div>

                    <div className="flex flex-col min-h-[60px] w-full bg-white">
                      {floor.toilets && floor.toilets.length > 0 ? (
                        floor.toilets.map((toilet) => (
                          <ToiletEmptyRow
                            key={toilet.spaceToiletId}
                            toilet={toilet}
                          />
                        ))
                      ) : (
                        <div className="w-full h-full bg-white"></div>
                      )}
                    </div>
                  </div>

                  {index < list.length - 1 && <HorizontalRule margin="0" />}
                </React.Fragment>
              );
            })
        ) : (
          <div className="sticky top-[72px] w-full h-40 flex flex-col items-center justify-center gap-2">
            <NoDataIcon />
            센서 데이터가 없어요
          </div>
        )}
        <HorizontalRule className="bg-black border-[2px]" margin="0" />
      </div>
      {simplePopupComponent}
      {toTopComponent}
      <ReportEmptyToilet popup={popup} onClose={() => setPopup("")} />
    </div>
  );
}

export type ToiletEmptyRowProps = { toilet: ToiletInfo };

export const getOpenCount = (compartments: ToiletInfo["compartments"]) => {
  return compartments.filter((compartment) => {
    return compartmentIsOpen(compartment);
  }).length;
};

export const ToiletEmptyRow = ({ toilet }: ToiletEmptyRowProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex flex-col justify-center w-full bg-white pl-5 h-full">
      <div
        className="flex items-center justify-between w-full text-sm font-medium min-h-[60px] gap-1"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <div className="design_min:w-[40px]">{toilet.toiletName}</div>
        <div className="flex items-center gap-2.5">
          {isCleaning(toilet) ? (
            <>
              <div className="font-semibold min-w-[40px]">청소중</div>
              <BigCleaningIcon />
            </>
          ) : (
            <ToiletStatus
              open={getOpenCount(toilet.compartments) || 0}
              compartment={toilet.compartments.length}
            />
          )}

          <div>
            {isOpen ? <ToiletEmptyDownArrow /> : <ToiletEmptyUpArrow />}
          </div>
        </div>
      </div>

      {isOpen &&
        toilet.compartments.length > 0 &&
        (isCleaning(toilet) ? (
          <div
            className="py-5 flex flex-col justify-center items-center text-sm gap-2.5"
            key={toilet.spaceToiletId}
          >
            <div className="flex flex-col">
              <LargeCleaningIcon />
            </div>
            <div className="flex flex-col text-center text-semibold text-lg leading-8">
              청소중입니다. <br />
              다른화장실을 이용하세요
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-4 py-5" key={toilet.spaceToiletId}>
            {toilet?.compartments.map((compartment, index) => (
              <React.Fragment
                key={
                  compartment.toiletCompartmentId ?? `toiletCompartment${index}`
                }
              >
                {compartmentIsOpen(compartment) ? (
                  <ToiletEmptyDoorOpenIcon />
                ) : (
                  <ToiletEmptyDoorCloseIcon />
                )}
              </React.Fragment>
            ))}
          </div>
        ))}
    </div>
  );
};

export type ToiletStatusProps = {
  open: number;
  compartment: number;
};

export const ToiletStatus = ({ open, compartment }: ToiletStatusProps) => {
  const rate = open / compartment;
  return (
    <div className="flex min-w-[80px] gap-1 items-center">
      <div className="font-semibold">
        사용가능{" "}
        <span
          className={clsx([
            {
              "text-brand-primary-red": rate <= 0.5 || compartment === 0,
              "text-brand-primary-good": rate > 0.5 && compartment !== 0,
            },
          ])}
        >
          {open}
        </span>
      </div>
      <div className="min-w-[24px]">
        {rate <= 0.5 || compartment === 0 ? <BigAngryIcon /> : <BigSmileIcon />}
      </div>
    </div>
  );
};

// 물음표 아이콘
export const ReportQuestionIcon = () => (
  <div className="h-[36px] w-[36px] flex items-center justify-center">
    <svg
      width="27"
      height="27"
      viewBox="0 0 27 27"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M25.5317 21.577L25.5317 1.40076L1.53174 1.40076L1.53174 21.577L10.1262 21.577L13.5317 25.4008L16.9394 21.577L25.5317 21.577Z"
        stroke="url(#paint0_linear_3455_42322)"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M8.55902 16.7514C8.55902 14.3722 10.785 12.4435 13.531 12.4435C16.2769 12.4435 18.5029 14.3722 18.5029 16.7514"
        stroke="url(#paint0_linear_3455_42322)"
        strokeWidth="1.5"
      />
      <path
        d="M18.4917 8.50726L18.5041 8.50726"
        stroke="url(#paint0_linear_3455_42322)"
        strokeWidth="1.5"
        strokeLinecap="square"
      />
      <path
        d="M8.55908 8.50726L8.57146 8.50726"
        stroke="url(#paint0_linear_3455_42322)"
        strokeWidth="1.5"
        strokeLinecap="square"
      />
      <defs>
        <linearGradient
          id="paint0_linear_3455_42322"
          x1="24"
          y1="18"
          x2="11.01"
          y2="20.8657"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#EA27C2" />
          <stop offset="1" stopColor="#E100A3" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_3455_42322"
          x1="30"
          y1="10"
          x2="12.9314"
          y2="23.1791"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#EA27C2" />
          <stop offset="1" stopColor="#E100A3" />
        </linearGradient>
        <linearGradient
          id="paint2_linear_3455_42322"
          x1="23"
          y1="4"
          x2="17.5737"
          y2="5.16383"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#EA27C2" />
          <stop offset="1" stopColor="#E100A3" />
        </linearGradient>
      </defs>
    </svg>
  </div>
);

// 세면대 막힘 아이콘
export const WashstandErrorIcon = () => (
  <svg
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14.5714 18H21.4286L24 32H12L14.5714 18Z"
      stroke="url(#paint0_linear_3455_42322)"
      strokeWidth="1.5"
    />
    <path
      d="M6 10H30V11C30 14.866 26.866 18 23 18H13C9.13401 18 6 14.866 6 11V10Z"
      stroke="url(#paint1_linear_3455_42322)"
      strokeWidth="1.5"
    />
    <path
      d="M18 10V6.5C18 5.11929 19.1193 4 20.5 4V4C21.8807 4 23 5.11929 23 6.5V7"
      stroke="url(#paint2_linear_3455_42322)"
      strokeWidth="1.5"
    />
    <defs>
      <linearGradient
        id="paint0_linear_3455_42322"
        x1="24"
        y1="18"
        x2="11.01"
        y2="20.8657"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EA27C2" />
        <stop offset="1" stopColor="#E100A3" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_3455_42322"
        x1="30"
        y1="10"
        x2="12.9314"
        y2="23.1791"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EA27C2" />
        <stop offset="1" stopColor="#E100A3" />
      </linearGradient>
      <linearGradient
        id="paint2_linear_3455_42322"
        x1="23"
        y1="4"
        x2="17.5737"
        y2="5.16383"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EA27C2" />
        <stop offset="1" stopColor="#E100A3" />
      </linearGradient>
    </defs>
  </svg>
);

// 변기 막힘 아이콘
export const ToiletErrorIcon = () => (
  <svg
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 30C8 28.8954 8.89543 28 10 28H26C27.1046 28 28 28.8954 28 30V32H8V30Z"
      stroke="url(#paint0_linear_3455_42319)"
      strokeWidth="1.5"
    />
    <path
      d="M18 20C13.5817 20 10 23.5817 10 28H26C26 23.5817 22.4183 20 18 20Z"
      stroke="url(#paint1_linear_3455_42319)"
      strokeWidth="1.5"
    />
    <rect
      x="16"
      y="16"
      width="4"
      height="4"
      stroke="url(#paint2_linear_3455_42319)"
      strokeWidth="1.5"
    />
    <path
      d="M16 6C16 4.89543 16.8954 4 18 4V4C19.1046 4 20 4.89543 20 6V20H16V6Z"
      stroke="url(#paint3_linear_3455_42319)"
      strokeWidth="1.5"
    />
    <defs>
      <linearGradient
        id="paint0_linear_3455_42319"
        x1="28"
        y1="28"
        x2="19.452"
        y2="39.0001"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EA27C2" />
        <stop offset="1" stopColor="#E100A3" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_3455_42319"
        x1="26"
        y1="20"
        x2="11.6415"
        y2="27.391"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EA27C2" />
        <stop offset="1" stopColor="#E100A3" />
      </linearGradient>
      <linearGradient
        id="paint2_linear_3455_42319"
        x1="20"
        y1="16"
        x2="15.7414"
        y2="17.0961"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EA27C2" />
        <stop offset="1" stopColor="#E100A3" />
      </linearGradient>
      <linearGradient
        id="paint3_linear_3455_42319"
        x1="20"
        y1="4"
        x2="15.478"
        y2="4.29096"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EA27C2" />
        <stop offset="1" stopColor="#E100A3" />
      </linearGradient>
    </defs>
  </svg>
);

export const ToiletEmptyDownArrow = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 15.9954L12 9.99536L6 15.9954"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);

export const ToiletEmptyUpArrow = () => (
  <svg
    className="transform rotate-180"
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 15.9954L12 9.99536L6 15.9954"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);

export const BigAngryIcon = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    data-testid="BigAngryIcon"
  >
    <circle cx="12" cy="12.9954" r="12" fill="#E7004B" />
    <path
      d="M7 17.9954C8.25492 16.7637 10.0309 15.9954 12 15.9954C13.9691 15.9954 15.7451 16.7637 17 17.9954"
      stroke="white"
    />
    <path d="M6 7.99536L10 11.9954" stroke="white" />
    <path d="M10 7.99536L6 11.9954" stroke="white" />
    <path d="M14 7.99536L18 11.9954" stroke="white" />
    <path d="M18 7.99536L14 11.9954" stroke="white" />
  </svg>
);

export const SmallAngryIcon = () => (
  <svg
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="8" cy="8.99536" r="8" fill="#E7004B" />
    <path
      d="M4.6665 12.3286C5.50312 11.5075 6.68713 10.9953 7.99984 10.9953C9.31255 10.9953 10.4966 11.5075 11.3332 12.3286"
      stroke="white"
    />
    <path d="M4 5.66211L6.66667 8.32878" stroke="white" />
    <path d="M6.6665 5.66211L3.99984 8.32878" stroke="white" />
    <path d="M9.3335 5.66211L12.0002 8.32878" stroke="white" />
    <path d="M12 5.66211L9.33333 8.32878" stroke="white" />
  </svg>
);

export const BigSmileIcon = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    data-testid="BigSmileIcon"
  >
    <circle cx="12" cy="12.9954" r="12" fill="#00CDC1" />
    <path
      d="M6 15.9954C7.5059 17.8429 9.63712 18.9954 12 18.9954C14.3629 18.9954 16.4941 17.8429 18 15.9954"
      stroke="white"
    />
    <circle cx="8" cy="10.9954" r="2" fill="white" />
    <circle cx="16" cy="10.9954" r="2" fill="white" />
  </svg>
);

export const SmallSmileIcon = () => (
  <svg
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="8" cy="8.99536" r="8" fill="#00CDC1" />
    <path
      d="M4 10.9954C5.00393 12.2271 6.42475 12.9954 8 12.9954C9.57525 12.9954 10.9961 12.2271 12 10.9954"
      stroke="white"
    />
    <circle cx="5.33333" cy="7.66195" r="1.33333" fill="white" />
    <ellipse cx="10.6668" cy="7.66195" rx="1.33333" ry="1.33333" fill="white" />
  </svg>
);

export const LargeCleaningIcon = () => (
  <svg
    width="34"
    height="35"
    viewBox="0 0 34 35"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    data-testid="LargeCleaningIcon"
  >
    <circle cx="17" cy="17.9954" r="17" fill="#FFB932" />
    <circle cx="17" cy="17.9954" r="17" fill="#FFB932" />
    <path
      d="M28.5767 25.4223L28.1223 26.0195H5.83435L5.42285 25.4223L16.9114 5.26807H17.0943L28.5767 25.4223Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M17.0017 17.6773V13.6101"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M16.9922 21.7246H17.0053"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M17.0017 17.6773V13.6101"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M16.9922 21.7246H17.0053"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
  </svg>
);

export const BigCleaningIcon = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    data-testid="BigCleaningIcon"
  >
    <circle cx="12" cy="12.9954" r="12" fill="#FFB932" />
    <circle cx="12" cy="12.9954" r="12" fill="#FFB932" />
    <path
      d="M20.172 18.238L19.8513 18.6595H4.1186L3.82812 18.238L11.9377 4.01147H12.0668L20.172 18.238Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M12.0011 12.7709V9.8999"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M11.9946 15.6279H12.0038"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M12.0011 12.7709V9.8999"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M11.9946 15.6279H12.0038"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
  </svg>
);

export const SmallCleaningIcon = () => (
  <svg
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="8" cy="8.99536" r="8" fill="#FFB932" />
    <circle cx="8" cy="8.99536" r="8" fill="#FFB932" />
    <path
      d="M13.4477 12.4905L13.2339 12.7715H2.74541L2.55176 12.4905L7.95814 3.0061H8.04423L13.4477 12.4905Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M8.0006 8.84563V6.93164"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M7.99605 10.7504H8.00223"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M8.00109 8.84563V6.93164"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M7.99654 10.7504H8.00271"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
  </svg>
);

export const ToiletEmptyLocationIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.97819 14.0275L4.13484 10.3128C3.39332 9.58128 2.89175 8.6545 2.69301 7.64861C2.49426 6.64273 2.6072 5.60252 3.01765 4.65843C3.4281 3.71433 4.1178 2.90837 5.00026 2.3416C5.88272 1.77484 6.91867 1.4725 7.97819 1.4725C9.03772 1.4725 10.0737 1.77484 10.9561 2.3416C11.8386 2.90837 12.5283 3.71433 12.9387 4.65843C13.3492 5.60252 13.4621 6.64273 13.2634 7.64862C13.0646 8.6545 12.5631 9.58128 11.8215 10.3128L7.97819 14.0275Z"
      fill="#B7B7B8"
      stroke="#B7B7B8"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.1743 6.93431C10.1743 8.14714 9.19108 9.13033 7.97825 9.13033C6.76542 9.13033 5.78223 8.14714 5.78223 6.93431C5.78223 5.72148 6.76542 4.73828 7.97825 4.73828C9.19108 4.73828 10.1743 5.72148 10.1743 6.93431Z"
      fill="white"
      stroke="white"
      strokeWidth="1.75"
    />
  </svg>
);

export const ToiletEmptyDoorOpenIcon = () => (
  <svg
    width="52"
    height="52"
    viewBox="0 0 52 52"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    data-testid="ToiletEmptyDoorOpenIcon"
  >
    <rect
      x="11"
      y="5"
      width="30"
      height="42"
      fill="url(#paint0_linear_1098_22085)"
      stroke="black"
      strokeWidth="2"
    />
    <path
      d="M11 47.1271V4.87289L31 2.14562V49.8544L11 47.1271Z"
      fill="white"
      stroke="black"
      strokeWidth="2"
    />
    <rect
      x="23"
      y="20"
      width="4"
      height="12"
      rx="2"
      stroke="black"
      strokeWidth="2"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1098_22085"
        x1="26"
        y1="4"
        x2="26"
        y2="48"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E7004B" />
        <stop offset="1" stopColor="#ED27B8" />
      </linearGradient>
    </defs>
  </svg>
);

export const ToiletEmptyDoorCloseIcon = () => (
  <svg
    width="52"
    height="52"
    viewBox="0 0 52 52"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    data-testid="ToiletEmptyDoorCloseIcon"
  >
    <g opacity="0.3">
      <rect
        x="10.75"
        y="4.75"
        width="30.5"
        height="42.5"
        fill="#E7E7E8"
        stroke="black"
        strokeWidth="1.5"
      />
      <path
        d="M16.5352 12.6923C16.5319 13.2359 16.6084 13.7632 16.7646 14.2743C16.9209 14.7821 17.1569 15.2378 17.4727 15.6415C17.7884 16.0451 18.1758 16.3576 18.6348 16.579L17.8828 17.5751C17.4206 17.344 17.0251 17.0217 16.6963 16.6083C16.3675 16.1916 16.1087 15.7066 15.9199 15.1532C15.7181 15.7489 15.443 16.2697 15.0947 16.7157C14.7464 17.1584 14.3281 17.5002 13.8398 17.7411L13.0684 16.7352C13.5534 16.5139 13.9619 16.1932 14.2939 15.7733C14.626 15.3501 14.8734 14.8733 15.0361 14.3427C15.2021 13.8088 15.2852 13.2587 15.2852 12.6923V11.3641H16.5352V12.6923ZM19.0254 10.6805H20.2754V14.1083H21.584V15.163H20.2754V19.5673H19.0254V10.6805ZM30.0117 15.8856H21.9062V14.8895H23.7324V13.8251C23.4036 13.6786 23.1514 13.4963 22.9756 13.2782C22.8031 13.0601 22.7168 12.8094 22.7168 12.5262C22.7168 12.1714 22.8486 11.8654 23.1123 11.6083C23.376 11.3511 23.752 11.1542 24.2402 11.0175C24.7285 10.8775 25.3014 10.8075 25.959 10.8075C26.61 10.8075 27.1781 10.8775 27.6631 11.0175C28.1514 11.1542 28.5273 11.3511 28.791 11.6083C29.0579 11.8654 29.1914 12.1714 29.1914 12.5262C29.1882 12.8127 29.097 13.0666 28.918 13.288C28.7389 13.5093 28.4818 13.6916 28.1465 13.8348V14.8895H30.0117V15.8856ZM22.834 17.9462C22.834 17.6076 22.9577 17.3195 23.2051 17.0819C23.4525 16.841 23.8089 16.6604 24.2744 16.5399C24.7432 16.4162 25.3014 16.3544 25.9492 16.3544C26.5872 16.3544 27.1374 16.4162 27.5996 16.5399C28.0651 16.6604 28.4215 16.841 28.6689 17.0819C28.9163 17.3195 29.0417 17.6076 29.0449 17.9462C29.0417 18.2912 28.9163 18.5842 28.6689 18.8251C28.4215 19.066 28.0667 19.2483 27.6045 19.3719C27.1423 19.4956 26.5905 19.5575 25.9492 19.5575C25.2982 19.5575 24.7399 19.4956 24.2744 19.3719C23.8089 19.2483 23.4525 19.066 23.2051 18.8251C22.9577 18.5842 22.834 18.2912 22.834 17.9462ZM24.0156 12.5262C24.0124 12.7671 24.1735 12.9494 24.499 13.0731C24.8278 13.1968 25.3031 13.2587 25.9248 13.2587C26.5661 13.2587 27.056 13.1984 27.3945 13.078C27.7331 12.9543 27.9023 12.7704 27.9023 12.5262C27.9023 12.2788 27.7347 12.09 27.3994 11.9598C27.0641 11.8296 26.584 11.7645 25.959 11.7645C25.3275 11.7645 24.8441 11.8296 24.5088 11.9598C24.1768 12.09 24.0124 12.2788 24.0156 12.5262ZM24.1035 17.9462C24.097 18.4019 24.7122 18.6298 25.9492 18.6298C26.5547 18.6298 27.012 18.5728 27.3213 18.4589C27.6338 18.3449 27.7917 18.174 27.7949 17.9462C27.7917 17.5165 27.1764 17.2984 25.9492 17.2919C25.3307 17.2951 24.8669 17.3505 24.5576 17.4579C24.2516 17.5653 24.1003 17.7281 24.1035 17.9462ZM26.9062 14.8895V14.1425C26.6393 14.1783 26.3236 14.1962 25.959 14.1962C25.627 14.1962 25.3047 14.1783 24.9922 14.1425V14.8895H26.9062ZM38.6543 15.7196H35.2168V16.3788C35.9948 16.4341 36.6003 16.5985 37.0332 16.8719C37.4661 17.1454 37.6842 17.51 37.6875 17.9657C37.6842 18.3042 37.5589 18.594 37.3115 18.8348C37.0641 19.0757 36.7077 19.258 36.2422 19.3817C35.7799 19.5054 35.2298 19.5673 34.5918 19.5673C33.944 19.5673 33.3857 19.5054 32.917 19.3817C32.4515 19.258 32.0951 19.0757 31.8477 18.8348C31.6003 18.5972 31.4766 18.3075 31.4766 17.9657C31.4766 17.5067 31.6947 17.1405 32.1309 16.8671C32.5671 16.5936 33.1823 16.4292 33.9766 16.3739V15.7196H30.5488V14.7137H38.6543V15.7196ZM30.9102 13.4052C31.4375 13.3596 31.8965 13.2684 32.2871 13.1317C32.6777 12.995 32.9886 12.8322 33.2197 12.6434C33.4541 12.4546 33.6104 12.2528 33.6885 12.038H31.291V11.0614H37.9219V12.038H35.5049C35.5798 12.2528 35.7327 12.4563 35.9639 12.6483C36.1982 12.8371 36.5124 12.9999 36.9062 13.1366C37.3034 13.2701 37.7721 13.3596 38.3125 13.4052L37.873 14.3817C37.0495 14.3036 36.3577 14.1197 35.7979 13.83C35.238 13.537 34.8359 13.1691 34.5918 12.7264C34.3509 13.1691 33.9505 13.537 33.3906 13.83C32.8307 14.1229 32.1406 14.3068 31.3203 14.3817L30.9102 13.4052ZM32.7461 17.9657C32.7428 18.1805 32.8958 18.3449 33.2051 18.4589C33.5176 18.5728 33.9798 18.6298 34.5918 18.6298C35.194 18.6298 35.6514 18.5728 35.9639 18.4589C36.2764 18.3449 36.4342 18.1805 36.4375 17.9657C36.4342 17.5262 35.819 17.3016 34.5918 17.2919C33.9733 17.2984 33.5094 17.357 33.2002 17.4677C32.8942 17.5751 32.7428 17.7411 32.7461 17.9657Z"
        fill="black"
      />
      <rect
        x="35.75"
        y="22.75"
        width="2.5"
        height="6.5"
        rx="1.25"
        stroke="black"
        strokeWidth="1.5"
      />
    </g>
  </svg>
);

export const ReportWarningIcon = () => (
  <svg
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.23975 18.2397C3.23975 26.524 9.95548 33.2397 18.2397 33.2397C26.524 33.2397 33.2397 26.524 33.2397 18.2397C33.2397 9.95548 26.524 3.23975 18.2397 3.23975C9.95548 3.23975 3.23975 9.95548 3.23975 18.2397Z"
      stroke="#1D1929"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M18.2407 11.7126V18.5713"
      stroke="black"
      strokeWidth="2"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M18.2334 23.3953H18.2457"
      stroke="black"
      strokeWidth="2"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);
